import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Lightning, Target, Medal, Clock, Fire, ShareNetwork } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import ShareStatsModal from "./ShareStatsModal";

const formatHMS = (seconds) => {
  const s = Math.max(0, Number(seconds) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
};

const Kpi = ({ icon: Icon, label, value, sub, color, testId }) => (
  <div className="card-tactical p-4 flex flex-col gap-1.5 transition-colors hover:border-white/20" data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="text-[9px] tracking-[0.25em] uppercase text-gray-400 font-bold">{label}</span>
      <Icon size={18} weight="duotone" style={{ color }} />
    </div>
    <div className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-black leading-none" style={{ color }}>{value}</div>
    {sub && <div className="text-[10px] text-gray-500">{sub}</div>}
  </div>
);

export default function PlayerKpiRow() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/dashboard/player-kpis")
      .then((r) => { if (alive) setKpis(r.data); })
      .catch(() => { if (alive) setKpis(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="player-kpi-row-loading">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="card-tactical p-4 animate-pulse h-24" />
        ))}
      </div>
    );
  }

  if (!kpis) return null;

  const rank = kpis.league_rank ? `#${kpis.league_rank}` : "—";
  const rankSub = kpis.league_rank_name ? kpis.league_rank_name : (kpis.league_rank ? "Liga" : "Sin liga aún");
  const streak = kpis.current_streak || 0;

  return (
    <div className="space-y-2" data-testid="player-kpi-row">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00]">Tus Estadísticas</h3>
          <span className="text-[10px] text-gray-500">Sumatoria total · {kpis.active_days_count || 0} días activos</span>
        </div>
        <Button onClick={() => setShareOpen(true)} size="sm" variant="outline" className="border-[#CCFF00]/40 text-[#CCFF00] hover:bg-[#CCFF00]/10 text-[10px] uppercase font-bold h-8" data-testid="share-stats-btn">
          <ShareNetwork size={12} className="mr-1" /> Compartir mis stats
        </Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Kpi icon={Lightning} label="Pts Retas" value={kpis.reta_points || 0} sub={`${kpis.reta_retas_played || 0} jugadas`} color="#CCFF00" testId="kpi-reta-points" />
        <Kpi icon={Target} label="Pts Marcadores" value={kpis.match_points || 0} sub="Liga 3/2/1/0" color="#34C759" testId="kpi-match-points" />
        <Kpi icon={Trophy} label="Marcadores" value={kpis.matches_count || 0} sub="partidos validados" color="#FFB800" testId="kpi-matches-count" />
        <Kpi icon={Medal} label="Rank Ligas" value={rank} sub={rankSub} color="#FF9500" testId="kpi-league-rank" />
        <Kpi icon={Clock} label="Tiempo Jugado" value={formatHMS(kpis.total_play_seconds || 0)} sub={`${formatHMS(kpis.match_seconds || 0)} m · ${formatHMS(kpis.reta_seconds || 0)} r`} color="#00A0FF" testId="kpi-total-time" />
        <Kpi icon={Fire} label="Racha actual" value={streak} sub={streak === 1 ? "día seguido" : "días seguidos"} color="#FF3B30" testId="kpi-streak" />
      </div>
      <ShareStatsModal open={shareOpen} onClose={() => setShareOpen(false)} kpis={kpis} user={user} />
    </div>
  );
}
